import React, { useRef } from "react";
import {
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  CheckIcon,
  XCircleIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import { Video, Folder } from "../types";

interface VideoUploadModalProps {
  show: boolean;
  onClose: () => void;
  editingVideo: Video | null;
  videoTitle: string;
  setVideoTitle: (title: string) => void;
  videoDescription: string;
  setVideoDescription: (desc: string) => void;
  videoSortOrder: number;
  setVideoSortOrder: (order: number) => void;
  videoFile: File | null;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  thumbnailFile: File | null;
  handleThumbnailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  thumbnailColor: string;
  setThumbnailColor: (color: string) => void;
  selectedFolderId: number | null;
  setSelectedFolderId: (id: number | null) => void;
  flattenedFolders: Array<{ id: number; name: string; depth: number }>;
  isUploading: boolean;
  uploadProgress: number;
  uploadStatus: "idle" | "uploading" | "success" | "error";
  uploadSpeed: number;
  eta: number;
  handleSubmit: (e: React.FormEvent) => void;
}

// Helper: get file icon based on file extension
const getFileIcon = (filename: string): string => {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  const videoExts = ["mp4", "mov", "avi", "wmv", "flv", "mkv", "webm"];
  const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "tiff"];

  if (videoExts.includes(ext)) return "🎬";
  if (ext === "pdf") return "📄";
  if (imageExts.includes(ext)) return "🖼️";
  if (["doc", "docx", "odt"].includes(ext)) return "📝";
  if (["xls", "xlsx", "csv", "ods"].includes(ext)) return "📊";
  if (["ppt", "pptx", "odp"].includes(ext)) return "📽️";
  if (ext === "txt") return "📃";
  return "📎";
};

const VideoUploadModal: React.FC<VideoUploadModalProps> = ({
  show,
  onClose,
  editingVideo,
  videoTitle,
  setVideoTitle,
  videoDescription,
  setVideoDescription,
  videoSortOrder,
  setVideoSortOrder,
  videoFile,
  handleFileChange,
  thumbnailFile,
  handleThumbnailChange,
  thumbnailColor,
  setThumbnailColor,
  selectedFolderId,
  setSelectedFolderId,
  flattenedFolders,
  isUploading,
  uploadProgress,
  uploadStatus,
  uploadSpeed,
  eta,
  handleSubmit,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  if (!show) return null;

  return (
    <div className="fixed inset-0 overflow-y-auto z-50">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white flex items-center">
              <ArrowUpTrayIcon className="h-6 w-6 mr-2" />
              {editingVideo ? "ແກ້ໄຂໄຟລ໌" : "ອັບໂຫລດໄຟລ໌ໃໝ່"}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                ຊື່ໄຟລ໌ *
              </label>
              <input
                type="text"
                required
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                placeholder="ປ້ອນຊື່ໄຟລ໌"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                ຄຳອະທິບາຍ
              </label>
              <textarea
                value={videoDescription}
                onChange={(e) => setVideoDescription(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                placeholder="ປ້ອນຄຳອະທິບາຍໄຟລ໌"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                ລຽງລຳດັບ
              </label>
              <input
                type="number"
                value={videoSortOrder}
                onChange={(e) =>
                  setVideoSortOrder(parseInt(e.target.value) || 0)
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                placeholder="ລຳດັບການສະແດງຜົນ"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                ເລືອກໂຟລເດີ
              </label>
              <select
                value={selectedFolderId || ""}
                onChange={(e) =>
                  setSelectedFolderId(
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
              >
                <option value="">-- ເລືອກໂຟລເດີ (ຖ້າຕ້ອງການ) --</option>
                {flattenedFolders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {"\u00A0".repeat(folder.depth * 2)}
                    {folder.depth > 0 ? "└ " : ""}
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {editingVideo ? "ປ່ຽນໄຟລ໌ (ຖ້າຕ້ອງການ)" : "ເລືອກໄຟລ໌ *"}
                </label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-1 flex justify-center px-4 py-4 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200"
                >
                  <div className="space-y-1 text-center">
                    <DocumentIcon className="mx-auto h-8 w-8 text-gray-400" />
                    <div className="text-xs text-gray-600">
                      {videoFile ? (
                        <span className="text-indigo-600 font-medium">
                          {getFileIcon(videoFile.name)} {videoFile.name}
                        </span>
                      ) : (
                        <span>ຄລິກເພື່ອເລືອກໄຟລ໌</span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400">
                      ວິດີໂອ, PDF, ຮູບ, ເອກະສານ
                    </p>
                  </div>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="video/*,image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.odt,.ods,.odp"
                  className="hidden"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  ຮູບໜ້າປົກ (Thumbnail)
                </label>
                <div
                  onClick={() => thumbnailInputRef.current?.click()}
                  className="mt-1 flex justify-center px-4 py-4 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200"
                >
                  <div className="space-y-1 text-center">
                    <PhotoIcon className="mx-auto h-8 w-8 text-gray-400" />
                    <div className="text-xs text-gray-600">
                      {thumbnailFile ? (
                        <span className="text-indigo-600 font-medium">
                          {thumbnailFile.name}
                        </span>
                      ) : (
                        <span>ເລືອກຮູບພາບ</span>
                      )}
                    </div>
                  </div>
                </div>
                <input
                  type="file"
                  ref={thumbnailInputRef}
                  onChange={handleThumbnailChange}
                  accept="image/*"
                  className="hidden"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ສີພື້ນຫຼັງໜ້າປົກ (ຫາກບໍ່ມີຮູບພາບ)
              </label>
              <div className="flex flex-wrap gap-3">
                {[
                  "#3B82F6",
                  "#EF4444",
                  "#10B981",
                  "#F59E0B",
                  "#8B5CF6",
                  "#EC4899",
                  "#6B7280",
                ].map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setThumbnailColor(color)}
                    className={`w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                      thumbnailColor === color
                        ? "border-black scale-110 shadow-md ring-2 ring-offset-1 ring-gray-300"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
                <div className="flex items-center ml-2 border border-gray-300 rounded-lg p-1 bg-gray-50">
                  <input
                    type="color"
                    value={thumbnailColor}
                    onChange={(e) => setThumbnailColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                    title="ສີກຳນົດເອງ"
                  />
                  <span className="text-xs font-mono ml-2 uppercase text-gray-500 pr-2">
                    {thumbnailColor}
                  </span>
                </div>
              </div>
            </div>

            {uploadStatus !== "idle" && (
              <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200">
                <div className="flex justify-between text-xs font-medium text-gray-700 mb-2">
                  <span className="flex items-center">
                    {uploadStatus === "uploading" ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-indigo-600 mr-2"></div>
                        ກຳລັງອັບໂຫລດ...
                      </>
                    ) : uploadStatus === "success" ? (
                      <>
                        <CheckIcon className="h-4 w-4 text-green-500 mr-1" />
                        ອັບໂຫລດສຳເລັດແລ້ວ!
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                        ອັບໂຫລດຜິດພາດ.
                      </>
                    )}
                  </span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      uploadStatus === "success"
                        ? "bg-green-500"
                        : uploadStatus === "error"
                          ? "bg-red-500"
                          : "bg-indigo-600"
                    }`}
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                {uploadStatus === "uploading" && (
                  <div className="mt-2 flex justify-between text-[10px] text-gray-500 tabular-nums">
                    <span>{(uploadSpeed / 1024 / 1024).toFixed(1)} MB/s</span>
                    <span>
                      ໃຊ້ເວລາປະມານ:{" "}
                      {eta > 60
                        ? `${Math.floor(eta / 60)}m ${Math.floor(eta % 60)}s`
                        : `${Math.floor(eta)}s`}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm"
              >
                ຍົກເລີກ
              </button>
              <button
                type="submit"
                disabled={isUploading}
                className={`px-8 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg transition-all duration-200 ${
                  isUploading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:shadow-xl active:scale-95"
                }`}
              >
                {isUploading
                  ? "ກຳລັງປະມວນຜົນ..."
                  : editingVideo
                    ? "ບັນທຶກການແກ້ໄຂ"
                    : "ອັບໂຫລດໄຟລ໌"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VideoUploadModal;
